import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import SVG from "@/assets/svg";
import CostumeButton from "../button";
import DialogueBox from "../dialogueBox";
import { DialogueStyled } from "../dialogueBox/dialogueStyled";

type ConfirmationOptions = {
  title?: string;
  subtitle?: string;
  positiveText?: string;
  negativeText?: string;
  onConfirm?: () => Promise<boolean>;
};

let showConfirmation: (options: ConfirmationOptions) => Promise<boolean>;

export const askConfirmation = (
  options: ConfirmationOptions
): Promise<boolean> => {
  return showConfirmation(options);
};

const ConfirmationDialog: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationOptions>({});
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(
    null
  );
  const [confirming, setConfirming] = useState(false);

  const handleClose = (result: boolean) => {
    if (confirming) return;
    setOpen(false);
    resolver?.(result);
  };

  const handleConfirm = async () => {
    if (!options.onConfirm) return handleClose(true);

    setConfirming(true);
    const done = await options.onConfirm().catch(() => false);
    setConfirming(false);

    if (!done) return;
    setOpen(false);
    resolver?.(true);
  };

  // Expose the function globally within React tree
  showConfirmation = (opts: ConfirmationOptions) => {
    return new Promise<boolean>((resolve) => {
      setOptions(opts);
      setOpen(true);
      setResolver(() => resolve);
    });
  };

  return (
    <DialogueBox onClose={() => handleClose(false)} open={open}>
      <DialogueStyled>
        <div>
          <Box className="confirmModal">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography id="modal-modal-title" variant="h3">
                {options.title?.startsWith("⚠️") ? (
                  <>
                    <SVG.Warning style={{ width: "24px", height: "24px" }} />
                    {options.title.replace("⚠️", "").trim()}
                  </>
                ) : (
                  options.title || "Are you sure?"
                )}
              </Typography>
              <Box>
                <SVG.Cross onClick={() => handleClose(false)} />
              </Box>
            </Box>

            <Box
              className="delete-confirm-msg"
              dangerouslySetInnerHTML={{
                __html:
                  options.subtitle ||
                  "This action cannot be reversed once done",
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <CostumeButton
                onClick={() => handleClose(false)}
                disabled={confirming}
                className="Cancel"
                style={{
                  margin: 0,
                }}
              >
                {options.negativeText || "Cancel"}
              </CostumeButton>
              <CostumeButton
                className="Delete"
                loading={confirming}
                onClick={handleConfirm}
              >
                {options.positiveText || "Confirm"}
              </CostumeButton>
            </Box>
          </Box>
        </div>
      </DialogueStyled>
    </DialogueBox>
  );
};

export default ConfirmationDialog;
